import { useState, useEffect, useCallback } from 'react';
import { BlockStack, InlineStack, Text, Select, TextField, Button, Banner, Badge, Box, Card, InlineGrid, Spinner, ButtonGroup, Divider } from '@shopify/polaris';
import { formatToCurrency } from '../../../shared/components/functions';
import { intentApi, intentMeta, formatIban, normalizeIban, isValidIban, signingTarget, IBAN_SOURCE_LABELS, PONTO_DASHBOARD_URL } from '../../../shared/components/banking/paymentIntents';
import { labelFor } from '../../../shared/components/approvals/approvalStatus';

/*
  "Pay through the bank" panel of the payment modal: creates a Ponto payment (a payment intent) for the
  supplier invoice. The invoice is NOT marked as paid here — it stays open until the bank's debit is
  matched by the banking integration, which then books it automatically.

  Props
    invoiceId        Faktuurin.faktuurinvolgnummer
    open             the modal is open (options are (re)loaded on every open)
    onAvailability   (bool) tells the modal whether the bank flow can be used for this invoice / user
    onSent           ({ intent, signingLink }) after a payment was created
    onIntentChange   after a pending payment was cancelled or refreshed (list refresh)
*/
const LAST_ACCOUNT_KEY = 'ponto.lastPaymentAccountId';
const OGM_PATTERN = /^\+{3}\d{3}\/\d{4}\/\d{5}\+{3}$|^\d{12}$/;

const readLastAccount = () => {
  try {
    return localStorage.getItem(LAST_ACCOUNT_KEY) || '';
  } catch (err) {
    return '';
  }
};
const storeLastAccount = (value) => {
  try {
    localStorage.setItem(LAST_ACCOUNT_KEY, value);
  } catch (err) {
    /* private mode */
  }
};

const openExternal = (url) => window.open(url, '_blank', 'noopener');
const todayIso = () => new Date().toLocaleDateString('sv-SE');

export default function BankPaymentPanel({ invoiceId, open, onAvailability, onSent, onIntentChange }) {
  const [options, setOptions] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [iban, setIban] = useState('');
  const [remittance, setRemittance] = useState('');
  const [executionDate, setExecutionDate] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busyIntentId, setBusyIntentId] = useState(null);
  const [intentMessage, setIntentMessage] = useState(null);

  const load = useCallback(async () => {
    const result = await intentApi(`/options/${invoiceId}`);
    if (!result.ok) {
      // 403 = no banking access, 503 = tables/Ponto missing: the modal falls back to the manual flow only
      setLoadError(result.status === 403 ? null : result.data?.msg || 'Bank payments are not available right now.');
      setOptions(null);
      onAvailability?.(false);
      return;
    }
    const data = result.data;
    const usable = data.ponto?.configured && Array.isArray(data.accounts) && data.accounts.length > 0;
    if (!usable) {
      setLoadError(data.ponto?.configured ? 'No active bank account is connected through Ponto.' : 'Ponto is not configured on this server.');
      setOptions(null);
      onAvailability?.(false);
      return;
    }
    setOptions(data);
    setLoadError(null);
    onAvailability?.(true);
    const remembered = readLastAccount();
    setAccountId(data.accounts.some((a) => String(a.BankAccountID) === remembered) ? remembered : String(data.accounts[0].BankAccountID));
    setAmount(Number(data.invoice.open || 0).toFixed(2));
    setIban(data.ibanSuggestions[0]?.iban || '');
    setRemittance(data.remittance?.value || '');
    setShowForm(data.activeIntents.length === 0);
  }, [invoiceId, onAvailability]);

  useEffect(() => {
    if (!open || !invoiceId) return;
    setOptions(null);
    setLoadError(null);
    setSent(null);
    setError(null);
    setIntentMessage(null);
    setExecutionDate('');
    load();
  }, [open, invoiceId, load]);

  if (loadError) {
    return (
      <Text tone="subdued" variant="bodySm">
        {loadError}
      </Text>
    );
  }
  if (!options) {
    return (
      <Box padding="400">
        <InlineStack gap="200" blockAlign="center">
          <Spinner size="small" accessibilityLabel="Loading bank accounts" />
          <Text tone="subdued">Checking bank accounts…</Text>
        </InlineStack>
      </Box>
    );
  }

  const { invoice, accounts, ibanSuggestions, approval, eligible, activeIntents, ponto } = options;
  const amountNumber = Number(String(amount).replace(',', '.'));
  const amountError = !(amountNumber > 0) ? 'Enter an amount' : amountNumber > Number(invoice.open) + 0.01 ? `Max ${formatToCurrency(invoice.open)} (open balance)` : undefined;
  const ibanError = iban && !isValidIban(iban) ? 'Invalid IBAN (checksum does not match)' : undefined;
  const dateError = executionDate && executionDate < todayIso() ? 'Cannot be in the past' : undefined;
  const approvalBlocked = approval.enforced && !approval.payable;
  const canSend = Boolean(accountId) && !amountError && Boolean(iban) && !ibanError && !dateError && eligible.euro && !invoice.paid && !approvalBlocked && !sending;
  const structured = OGM_PATTERN.test(remittance.replace(/\s/g, ''));
  // the bank refuses to execute a transfer the account cannot cover (balances are as of the last sync)
  const selectedAccount = accounts.find((a) => String(a.BankAccountID) === accountId);
  const balanceShort = selectedAccount?.AvailableBalance != null && amountNumber > Number(selectedAccount.AvailableBalance) + 0.005;
  const selectedSuggestion = ibanSuggestions.find((s) => s.iban === iban);
  const ibanHelp = selectedSuggestion
    ? selectedSuggestion.label
    : iban
    ? 'Typed by hand — it is stored on the invoice and suggested for this supplier next time'
    : ibanSuggestions.length
    ? 'Pick one of the known accounts below or type the IBAN from the invoice'
    : 'No IBAN known for this supplier yet — copy it from the invoice';

  const send = async () => {
    setSending(true);
    setError(null);
    const result = await intentApi('', {
      method: 'POST',
      body: {
        faktuurinId: invoiceId,
        accountId: Number(accountId),
        amount: amountNumber,
        iban,
        remittance,
        executionDate: executionDate || null,
        allowDuplicate: activeIntents.length > 0
      }
    });
    setSending(false);
    if (result.ok) {
      storeLastAccount(accountId);
      setSent(result.data);
      onSent?.(result.data);
    } else {
      setError(result.data?.msg || 'The payment could not be created.');
    }
  };

  const intentAction = async (intent, action) => {
    setBusyIntentId(intent.PontoPaymentID);
    setIntentMessage(null);
    const result = await intentApi(`/${intent.PontoPaymentID}/${action}`, { method: 'POST' });
    setBusyIntentId(null);
    if (!result.ok) {
      setIntentMessage({ tone: 'critical', text: result.data?.msg || `Could not ${action} the payment.` });
      return;
    }
    if (action === 'cancel') setIntentMessage({ tone: 'success', text: 'Payment cancelled — the invoice is still open.' });
    if (action === 'refresh') setIntentMessage({ tone: 'info', text: `Status: ${intentMeta(result.data.state).label.toLowerCase()} (${result.data.Status}).` });
    onIntentChange?.();
    // reload everything: pending list, suggestions (the cancelled intent disappears)
    const fresh = await intentApi(`/options/${invoiceId}`);
    if (fresh.ok) {
      setOptions(fresh.data);
      if (fresh.data.activeIntents.length === 0) setShowForm(true);
    }
  };

  const header = (
    <InlineStack align="space-between" blockAlign="center">
      <Text variant="headingMd" as="h3">
        🏦 Pay through the bank
      </Text>
      <Badge tone={ponto.environment === 'live' ? 'success' : 'attention'}>{`Ponto${ponto.environment ? ` · ${ponto.environment}` : ''}`}</Badge>
    </InlineStack>
  );

  if (sent) {
    const intent = sent.intent || {};
    return (
      <Card>
        <BlockStack gap="400">
          {header}
          <Banner
            tone="success"
            title={`${formatToCurrency(intent.Amount)} sent to the bank`}
            action={
              sent.signingLink
                ? { content: 'Sign in Ponto', onAction: () => openExternal(sent.signingLink) }
                : { content: 'Open Ponto to sign', onAction: () => openExternal(sent.dashboardUrl || PONTO_DASHBOARD_URL) }
            }
          >
            <BlockStack gap="100">
              <Text as="p">
                To {formatIban(intent.CreditorIBAN)} from {intent.accountDescription || formatIban(intent.accountReference)}.{' '}
                {sent.signingLink
                  ? 'The payment still has to be signed in Ponto before the bank executes it.'
                  : `It now waits in the Ponto dashboard under the account's Payments → To sign, where a signer of the organisation signs it.`}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                The invoice stays open and is marked as paid automatically once the bank's debit is matched.
              </Text>
            </BlockStack>
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  const pendingBlock =
    activeIntents.length === 0 ? null : (
      <Banner tone={activeIntents[0].state === 'awaiting_signature' ? 'warning' : 'info'} title={`A bank payment is already pending — ${intentMeta(activeIntents[0].state).label.toLowerCase()}`}>
        <BlockStack gap="200">
          {activeIntents.map((intent) => (
            <InlineStack key={intent.PontoPaymentID} align="space-between" blockAlign="center" gap="200" wrap>
              <Text as="span">
                {formatToCurrency(intent.Amount)} to {formatIban(intent.CreditorIBAN)} · sent {String(intent.CreatedAt).substring(0, 10)}
                {intent.createdBy ? ` by ${intent.createdBy}` : ''}
              </Text>
              <ButtonGroup>
                {intent.state === 'awaiting_signature' ? (
                  <Button size="slim" onClick={() => openExternal(signingTarget(intent).url)}>
                    {signingTarget(intent).label}
                  </Button>
                ) : null}
                <Button size="slim" loading={busyIntentId === intent.PontoPaymentID} onClick={() => intentAction(intent, 'refresh')}>
                  Refresh
                </Button>
                {intent.state === 'awaiting_signature' ? (
                  <Button size="slim" tone="critical" loading={busyIntentId === intent.PontoPaymentID} onClick={() => intentAction(intent, 'cancel')}>
                    Cancel
                  </Button>
                ) : null}
              </ButtonGroup>
            </InlineStack>
          ))}
          {!showForm ? (
            <Button variant="plain" onClick={() => setShowForm(true)}>
              Send another payment anyway
            </Button>
          ) : null}
        </BlockStack>
      </Banner>
    );

  const form = !showForm ? null : (
    <BlockStack gap="300">
      {invoice.paid ? <Banner tone="info">This invoice is already marked as paid.</Banner> : null}
      {!eligible.euro ? <Banner tone="critical">This invoice is in {invoice.currency}. Ponto only sends SEPA transfers in EUR — register this payment manually.</Banner> : null}
      {!approval.payable ? (
        <Banner
          tone={approvalBlocked ? 'critical' : 'warning'}
          title={`Not fully approved (${labelFor(approval.status).toLowerCase()}${approval.assigneeAfkorting ? `, ${approval.assigneeAfkorting}` : ''})`}
        >
          {approvalBlocked ? 'Payments are blocked until the invoice is approved.' : 'Sending is still possible; it is logged on the invoice timeline.'}
        </Banner>
      ) : null}
      <Select
        label="From account"
        options={accounts.map((a) => ({
          label: `${a.Description || a.Product || 'Account'} · ${formatIban(a.Reference)}${a.AvailableBalance != null ? ` · ${formatToCurrency(a.AvailableBalance)}` : ''}`,
          value: String(a.BankAccountID)
        }))}
        value={accountId}
        onChange={setAccountId}
      />
      <InlineGrid columns={2} gap="300">
        <TextField
          label="Amount"
          prefix="€"
          type="number"
          step="0.01"
          value={amount}
          onChange={setAmount}
          error={amountError}
          autoComplete="off"
          helpText={`Open: ${formatToCurrency(invoice.open)}`}
        />
        <TextField label="Execution date" type="date" min={todayIso()} value={executionDate} onChange={setExecutionDate} error={dateError} autoComplete="off" helpText="Empty = right after signing" />
      </InlineGrid>
      {balanceShort ? (
        <Banner tone="warning">
          This amount exceeds the available balance of the selected account ({formatToCurrency(selectedAccount.AvailableBalance)} at the last sync). The bank will refuse to execute the transfer unless
          the account is funded first.
        </Banner>
      ) : null}
      <TextField label="Beneficiary IBAN" value={iban} onChange={(value) => setIban(normalizeIban(value))} error={ibanError} helpText={ibanHelp} autoComplete="off" monospaced />
      {ibanSuggestions.length > 1 || (ibanSuggestions.length === 1 && ibanSuggestions[0].iban !== iban) ? (
        <InlineStack gap="100" wrap>
          {ibanSuggestions.map((s) => (
            <Button key={s.iban} size="micro" pressed={s.iban === iban} onClick={() => setIban(s.iban)}>
              {`${s.formatted} · ${IBAN_SOURCE_LABELS[s.source] || s.source}`}
            </Button>
          ))}
        </InlineStack>
      ) : null}
      <TextField
        label="Payment message"
        value={remittance}
        onChange={setRemittance}
        maxLength={140}
        autoComplete="off"
        helpText={structured ? 'Structured communication (OGM) — the supplier books the payment automatically' : 'Free text, max 140 characters'}
      />
      <Text as="p" tone="subdued" variant="bodySm">
        Beneficiary: <b>{invoice.supplierName || 'unknown supplier'}</b>
      </Text>
      {error ? (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      ) : null}
      <Button variant="primary" fullWidth loading={sending} disabled={!canSend} onClick={send}>
        {amountNumber > 0 ? `Send ${formatToCurrency(amountNumber)} to the bank` : 'Send to the bank'}
      </Button>
      <Text as="p" tone="subdued" variant="bodySm">
        The payment is created in Ponto and still has to be signed there. The invoice stays open until the bank confirms the transfer — it is then marked as paid automatically.
      </Text>
    </BlockStack>
  );

  // outcome of a cancel / refresh, shown outside the pending block (which disappears after a cancel)
  const intentFeedback = intentMessage ? (
    <Banner tone={intentMessage.tone === 'critical' ? 'critical' : intentMessage.tone === 'success' ? 'success' : 'info'} onDismiss={() => setIntentMessage(null)}>
      {intentMessage.text}
    </Banner>
  ) : null;

  return (
    <Card>
      <BlockStack gap="400">
        {header}
        {intentFeedback}
        {pendingBlock}
        {pendingBlock && form ? <Divider /> : null}
        {form}
      </BlockStack>
    </Card>
  );
}
